import CloseIcon from '@mui/icons-material/Close'
import { Alert, AlertTitle, Container, IconButton } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import React from 'react'
import { i18n } from '../index/index'

interface FrontpageAlertProps {
  date: string
  restaurantMenu?: {
    categories: {
      id: string
      name: string
    }[]
  } | null
  restaurantMenuPeriods: {
    categoryId: string
    periodName: string
    startDate: string
    endDate: string
  }[]
  alertOpen: boolean
  closeAlert: () => void
}

export default function FrontpageAlert({
  date,
  restaurantMenu,
  restaurantMenuPeriods,
  alertOpen,
  closeAlert,
}: FrontpageAlertProps) {
  const restaurantMenuAlerts = deduceRestaurantMenuAlerts(date, restaurantMenu, restaurantMenuPeriods)
  if (!alertOpen || restaurantMenuAlerts.length === 0) {
    return <></>
  }
  return (
    <Container sx={{ marginBottom: 2 }}>
      <Alert
        severity='warning'
        sx={{ whiteSpace: 'pre-line' }}
        action={
          <IconButton aria-label='close' color='inherit' size='small' onClick={closeAlert}>
            <CloseIcon fontSize='inherit' />
          </IconButton>
        }
      >
        <AlertTitle>{i18n.t('restaurantMenu.alerts.title')}</AlertTitle>
        {restaurantMenuAlerts.join('\n')}
      </Alert>
    </Container>
  )
}

function deduceRestaurantMenuAlerts(
  date: string,
  restaurantMenu: { categories: { id: string; name: string }[] } | null | undefined,
  restaurantMenuPeriods: { categoryId: string; periodName: string; startDate: string; endDate: string }[],
): string[] {
  if (!restaurantMenu) {
    return []
  }
  const twoWeeksFromDate = moment(date).add(14, 'days')
  const alerts = []
  for (const menuCategory of restaurantMenu.categories) {
    const periodsForCategory = restaurantMenuPeriods.filter((period) => period.categoryId === menuCategory.id)
    if (periodsForCategory.length === 0) {
      alerts.push(i18n.t('restaurantMenu.alerts.categoryHasNoPeriodsDefined', { categoryName: menuCategory.name }))
      continue
    }
    const sortedPeriodsByEndDate = _.sortBy(periodsForCategory, (period) =>
      new Date(period.endDate).getTime(),
    ).reverse()
    const latestPeriod = sortedPeriodsByEndDate[0]
    const isAboutToExpire = moment(latestPeriod.endDate).isSameOrBefore(twoWeeksFromDate)
    if (isAboutToExpire) {
      alerts.push(
        i18n.t('restaurantMenu.alerts.categoryPeriodIsAboutToExpire', {
          categoryName: menuCategory.name,
          periodName: latestPeriod.periodName,
          endDate: moment(latestPeriod.endDate).format('DD.MM.YYYY'),
        }),
      )
      continue
    }
    const hasExpired = moment(latestPeriod.endDate).isBefore(date)
    if (hasExpired) {
      alerts.push(i18n.t('restaurantMenu.alerts.noActiveCategoryPeriods', { categoryName: menuCategory.name }))
    }
  }
  return alerts
}
